import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Clock, Building2, MapPin, Search, DollarSign, Eye, GraduationCap, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ExamCenterAppsTab({ token }) {
  const [apps, setApps] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showNominationsModal, setShowNominationsModal] = useState(null);
  const [selectedTab, setSelectedTab] = useState('applications');

  const REGISTRATION_FEE = 150000;

  const fetchData = async () => {
    try {
      const [appsRes, nomsRes] = await Promise.all([
        axios.get(`${API}/admin/examination-center-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/center-nominations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setApps(appsRes.data);
      setNominations(nomsRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status, reason = null) => {
    try {
      const data = { status };
      if (reason) data.rejection_reason = reason;
      await axios.put(`${API}/admin/examination-center-applications/${id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Application ${status.replace('_', ' ')}`);
      fetchData();
      setShowRejectModal(null);
      setRejectionReason('');
    } catch (err) { toast.error('Failed to update'); }
  };

  const updatePaymentStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/admin/examination-center-applications/${appId}/payment`, { payment_status: status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Payment status updated');
      fetchData();
    } catch (err) { toast.error('Failed to update payment'); }
  };

  const updateNominationPayment = async (nomId, status) => {
    try {
      await axios.put(`${API}/admin/center-nominations/${nomId}/payment`, { payment_status: status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Nomination payment status updated');
      fetchData();
    } catch (err) { toast.error('Failed to update payment'); }
  };

  const statusColor = (s) => {
    const m = { 
      submitted: 'bg-blue-100 text-blue-700', 
      in_review: 'bg-amber-100 text-amber-700',
      incomplete: 'bg-orange-100 text-orange-700',
      resubmission: 'bg-purple-100 text-purple-700',
      approved: 'bg-green-100 text-green-700', 
      rejected: 'bg-red-100 text-red-700'
    };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  const paymentStatusColor = (s) => {
    const m = { paid: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', pending_verification: 'bg-blue-100 text-blue-700' };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading...</p>;

  const approvedCenters = apps.filter(a => a.status === 'approved');
  const pendingApps = apps.filter(a => a.status !== 'approved' && a.status !== 'rejected');

  return (
    <div data-testid="admin-examination-center-tab">
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-4">
        <Button onClick={() => setSelectedTab('applications')} className={`rounded-sm ${selectedTab === 'applications' ? 'bg-[#1B2A4A] text-white' : 'bg-slate-100 text-slate-700'}`}>
          Applications ({apps.length})
        </Button>
        <Button onClick={() => setSelectedTab('nominations')} className={`rounded-sm ${selectedTab === 'nominations' ? 'bg-[#1B2A4A] text-white' : 'bg-slate-100 text-slate-700'}`}>
          Student Nominations ({nominations.length})
        </Button>
      </div>

      {selectedTab === 'applications' ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{apps.length}</p><p className="text-xs text-blue-600">Total</p></CardContent></Card>
            <Card className="bg-amber-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-700">{pendingApps.length}</p><p className="text-xs text-amber-600">Pending</p></CardContent></Card>
            <Card className="bg-green-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-700">{approvedCenters.length}</p><p className="text-xs text-green-600">Approved</p></CardContent></Card>
            <Card className="bg-[#1B2A4A]"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-white">₹{(approvedCenters.filter(a => a.registration_fee_paid).length * REGISTRATION_FEE).toLocaleString()}</p><p className="text-xs text-slate-300">Fees Collected</p></CardContent></Card>
          </div>

          {/* Reject Modal */}
          {showRejectModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-md mx-4">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Reject Application</h3>
                  <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm" rows={3} placeholder="Rejection reason..." />
                  <div className="flex gap-2 justify-end mt-4">
                    <Button variant="outline" onClick={() => { setShowRejectModal(null); setRejectionReason(''); }} className="rounded-sm">Cancel</Button>
                    <Button onClick={() => updateStatus(showRejectModal, 'rejected', rejectionReason)} className="bg-red-600 text-white rounded-sm">Reject</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Applications Table */}
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Center Name</TableHead>
                <TableHead className="text-xs font-bold">Center ID</TableHead>
                <TableHead className="text-xs font-bold">Country</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Reg. Fee (₹{REGISTRATION_FEE.toLocaleString()})</TableHead>
                <TableHead className="text-xs font-bold">Nominations</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.map((app) => {
                const centerNoms = nominations.filter(n => n.center_id === (app.center_id || app.id));
                return (
                  <TableRow key={app.id}>
                    <TableCell>
                      <p className="text-sm font-medium text-[#1B2A4A]">{app.center_name}</p>
                      <p className="text-xs text-slate-500">{app.contact_person || app.email}</p>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{app.center_id || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{app.country}</TableCell>
                    <TableCell><Badge className={`text-xs rounded-sm ${statusColor(app.status)}`}>{app.status?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell>
                      {app.status === 'approved' ? (
                        app.registration_fee_paid ? (
                          <Badge className="bg-green-100 text-green-700">Paid</Badge>
                        ) : (
                          <div className="flex gap-1">
                            <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                            <Button size="sm" className="h-5 px-2 text-[10px] bg-green-600 text-white rounded-sm" onClick={() => updatePaymentStatus(app.id, 'paid')}>
                              Mark Paid
                            </Button>
                          </div>
                        )
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {centerNoms.length > 0 ? (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm" onClick={() => setShowNominationsModal(app)}>
                          {centerNoms.length} nominations
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" className="h-6 px-2 bg-green-600 text-white text-xs rounded-sm" onClick={() => updateStatus(app.id, 'approved')}>
                          <CheckCircle className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-amber-400 text-amber-600" onClick={() => updateStatus(app.id, 'in_review')}>
                          Review
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm border-red-300 text-red-600" onClick={() => setShowRejectModal(app.id)}>
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Nominations Modal */}
          {showNominationsModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNominationsModal(null)}>
              <Card className="w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <CardHeader className="bg-[#1B2A4A] text-white">
                  <CardTitle>{showNominationsModal.center_name} - Student Nominations</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {nominations.filter(n => n.center_id === (showNominationsModal.center_id || showNominationsModal.id)).map(nom => (
                    <div key={nom.id} className="flex items-center justify-between p-3 border-b last:border-b-0">
                      <div>
                        <p className="font-medium">{nom.full_name}</p>
                        <p className="text-xs text-slate-500">{nom.email} • {nom.examination}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-medium">₹{nom.base_fee?.toLocaleString()}</p>
                        <Badge className={paymentStatusColor(nom.payment_status)}>{nom.payment_status}</Badge>
                        {nom.payment_status !== 'paid' && (
                          <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white rounded-sm" onClick={() => updateNominationPayment(nom.id, 'paid')}>
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      ) : (
        /* Nominations Tab */
        <div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{nominations.length}</p><p className="text-xs text-blue-600">Total</p></CardContent></Card>
            <Card className="bg-amber-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-700">{nominations.filter(n => n.payment_status === 'pending').length}</p><p className="text-xs text-amber-600">Pending Payment</p></CardContent></Card>
            <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{nominations.filter(n => n.payment_status === 'pending_verification').length}</p><p className="text-xs text-blue-600">Pending Verification</p></CardContent></Card>
            <Card className="bg-green-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-700">{nominations.filter(n => n.payment_status === 'paid').length}</p><p className="text-xs text-green-600">Paid</p></CardContent></Card>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Student</TableHead>
                <TableHead className="text-xs font-bold">Center</TableHead>
                <TableHead className="text-xs font-bold">Examination</TableHead>
                <TableHead className="text-xs font-bold">Fee</TableHead>
                <TableHead className="text-xs font-bold">Payment</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nominations.map((nom) => (
                <TableRow key={nom.id}>
                  <TableCell>
                    <p className="text-sm font-medium">{nom.full_name}</p>
                    <p className="text-xs text-slate-500">{nom.email}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{nom.center_name}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-700">{nom.examination}</Badge></TableCell>
                  <TableCell className="font-medium">₹{nom.base_fee?.toLocaleString()}</TableCell>
                  <TableCell><Badge className={paymentStatusColor(nom.payment_status)}>{nom.payment_status?.replace('_', ' ')}</Badge></TableCell>
                  <TableCell>
                    {nom.payment_status === 'pending_verification' && (
                      <div className="flex gap-1">
                        <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white rounded-sm" onClick={() => updateNominationPayment(nom.id, 'paid')}>
                          <CheckCircle className="h-3 w-3 mr-1" />Verify & Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm text-red-600" onClick={() => updateNominationPayment(nom.id, 'pending')}>
                          Reject
                        </Button>
                      </div>
                    )}
                    {nom.payment_status === 'pending' && (
                      <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white rounded-sm" onClick={() => updateNominationPayment(nom.id, 'paid')}>
                        Mark Paid
                      </Button>
                    )}
                    {nom.payment_status === 'paid' && (
                      <span className="text-xs text-green-600">✓ Complete</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default ExamCenterAppsTab;
